/** Lienzo 16:9 lógico (coordenadas 0–1 relativas al lienzo). */

import {
  parseColumnsRow,
  stringifyColumnsRow,
  emptyColumnsRow,
  cloneWidgetTree,
} from "@/lib/onboardingColumnsRow";

export const CANVAS_LOGICAL_W = 960;
export const CANVAS_LOGICAL_H = 540;

function _clampNum(v, min, max, fallback) {
  const n = Number(v);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

export function widgetSizeKeysForPreview(previewDevice) {
  if (previewDevice === "responsive") return { wKey: "wResponsive", hKey: "hResponsive" };
  return { wKey: "w", hKey: "h" };
}

/**
 * Propiedades de layout que tienen override independiente por dispositivo.
 * Clave en el widget base → clave de la variante responsive.
 */
const RESPONSIVE_LAYOUT_KEYS = {
  w: "wResponsive",
  h: "hResponsive",
  fontSizeLevel: "fontSizeLevelResponsive",
  textAlign: "textAlignResponsive",
  verticalAlign: "verticalAlignResponsive",
};

/** Devuelve las props de layout efectivas según dispositivo (responsive cae a desktop si no existe override). */
export function readWidgetSizeForPreview(widget, previewDevice) {
  const wd = widget && typeof widget === "object" ? widget : {};
  if (previewDevice === "responsive") {
    const rw = Number(wd.wResponsive);
    const rh = Number(wd.hResponsive);
    const rfsl = Number(wd.fontSizeLevelResponsive);
    const rta = wd.textAlignResponsive;
    const rva = wd.verticalAlignResponsive;
    return {
      w: Number.isFinite(rw) ? rw : Number(wd.w),
      h: Number.isFinite(rh) ? rh : Number(wd.h),
      fontSizeLevel: Number.isFinite(rfsl) ? rfsl : Number(wd.fontSizeLevel),
      textAlign: rta != null ? rta : (wd.textAlign || "left"),
      verticalAlign: rva != null ? rva : (wd.verticalAlign || "start"),
    };
  }
  return {
    w: Number(wd.w),
    h: Number(wd.h),
    fontSizeLevel: Number(wd.fontSizeLevel),
    textAlign: wd.textAlign || "left",
    verticalAlign: wd.verticalAlign || "start",
  };
}

/**
 * Redirige las claves de layout a sus variantes responsive cuando el dispositivo activo es
 * "responsive". Solo actúa sobre las claves con override independiente; el resto pasa tal cual.
 * Usado tanto para resize (solo w/h) como para cualquier patch del panel de propiedades.
 */
export function mapWidgetSizePatchForPreview(partial, previewDevice) {
  if (!partial || typeof partial !== "object") return partial;
  if (previewDevice !== "responsive") return partial;
  const out = { ...partial };
  for (const [baseKey, respKey] of Object.entries(RESPONSIVE_LAYOUT_KEYS)) {
    if (Object.prototype.hasOwnProperty.call(out, baseKey)) {
      out[respKey] = out[baseKey];
      delete out[baseKey];
    }
  }
  return out;
}

/** Niveles 1 (H6) – 5 (H1); escala con `cqmin` respecto al widget. */
export const ONBOARDING_HEADING_FONT_LEVELS = [
  "clamp(0.68rem, 3.2cqmin, 0.95rem)",
  "clamp(0.76rem, 4cqmin, 1.12rem)",
  "clamp(0.86rem, 5cqmin, 1.38rem)",
  "clamp(0.96rem, 6.2cqmin, 1.72rem)",
  "clamp(1.2rem, 10cqmin, 3.5rem)",
];

export const ONBOARDING_BODY_FONT_LEVELS = [
  "clamp(0.62rem, 2.4cqmin, 0.82rem)",
  "clamp(0.68rem, 2.7cqmin, 0.92rem)",
  "clamp(0.74rem, 3.1cqmin, 1.05rem)",
  "clamp(0.82rem, 3.6cqmin, 1.18rem)",
  "clamp(0.9rem, 4.1cqmin, 1.32rem)",
];

/** Tamaños de emoji: 5 niveles de H6 (pequeño) a H1 (grande), escala con `cqmin`. */
export const ONBOARDING_EMOJI_SIZE_LEVELS = [
  "clamp(1.4rem, 10cqmin, 2.5rem)",
  "clamp(2rem, 16cqmin, 4rem)",
  "clamp(3rem, 24cqmin, 6rem)",
  "clamp(4rem, 36cqmin, 9rem)",
  "clamp(6rem, 52cqmin, 14rem)",
];

/**
 * Convierte un carácter emoji a la URL de Noto Emoji Animation (Google Fonts CDN).
 * Formato: https://fonts.gstatic.com/s/e/notoemoji/latest/{codepoints}/512.webp
 * Los codepoints se separan con "_" y se excluye el selector de variación U+FE0F.
 */
export function emojiToNotoUrl(emoji, format = "512.webp") {
  if (!emoji) return null;
  const points = [];
  for (const ch of emoji) {
    const cp = ch.codePointAt(0);
    if (cp !== 0xfe0f) points.push(cp.toString(16));
  }
  if (!points.length) return null;
  return `https://fonts.gstatic.com/s/e/notoemoji/latest/${points.join("_")}/${format}`;
}

export function normalizedFontSizeLevel(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return 3;
  return Math.max(1, Math.min(5, n));
}

/** Etiqueta semántica por nivel (5 pasos: H6→…→H1, sin H2 intermedio). */
export function headingSemanticTagFromLevel(level) {
  const tags = ["h6", "h5", "h4", "h3", "h1"];
  const i = normalizedFontSizeLevel(level) - 1;
  return tags[i] || "h3";
}

/** Preset de color por defecto si `textColor` no es hex #RRGGBB. */
const TYPO_COLOR_DEFAULTS = {
  heading: "#171717",
  body: "#404040",
  sticky: "#422006",
  caption: "#525252",
  chip: "#5b21b6",
};

/**
 * @param {string} preset heading | body | sticky | caption | chip
 */
export function onboardingTextColorFromWidget(w, preset) {
  const c = String(w?.textColor || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(c)) return c;
  return TYPO_COLOR_DEFAULTS[preset] || TYPO_COLOR_DEFAULTS.body;
}

/** Widgets con texto editable al que aplican tamaño/color de tipografía. */
export function widgetSupportsTextTypography(type) {
  return ["heading", "text", "bullets", "sticky", "image", "chips", "emoji"].includes(String(type || ""));
}

/** Forma del marco del widget imagen: rect libre, cuadrado o círculo perfecto. */
export function normalizedImageBoxShape(v) {
  return v === "square" || v === "circle" ? v : "rect";
}

/** h normalizado (0–1) coherente con w para un cuadrado en píxeles en lienzo 16:9. */
export function imageSquareNormalizedHFromW(wFrac) {
  const wf = Math.max(0.04, Math.min(1, Number(wFrac) || 0));
  return Math.max(0.03, Math.min(1, wf * (CANVAS_LOGICAL_W / CANVAS_LOGICAL_H)));
}

/** Opciones de maquetación por diapositiva (gap, paddings, ancho útil en 1 columna). */
export function getSlideLayout(slide) {
  const s = slide && typeof slide === "object" ? slide : {};
  return {
    layoutColumns: s.layoutColumns === 2 || s.layoutColumns === 3 ? s.layoutColumns : 1,
    widgetGapRem: _clampNum(s.widgetGapRem, 0.15, 3, 0.45),
    widgetMaxWidthPx: Math.round(_clampNum(s.widgetMaxWidthPx, 280, 1200, 680)),
    canvasPadDesktopPx: Math.round(_clampNum(s.canvasPadDesktopPx, 0, 96, 14)),
    canvasPadMobilePx: Math.round(_clampNum(s.canvasPadMobilePx, 0, 80, 10)),
  };
}

const WIDGET_PAD_MAX = 24;

export function clampWidgetPaddingUnit(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(WIDGET_PAD_MAX, n));
}

/** Margen externo del widget (misma escala que padding; admite negativos). */
export function clampWidgetMarginUnit(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return 0;
  return Math.max(-WIDGET_PAD_MAX, Math.min(WIDGET_PAD_MAX, n));
}

/** Padding interno del slot (cada unidad = 0,125 rem; máx. 3 rem). */
export function widgetInnerPaddingStyle(w) {
  const t = clampWidgetPaddingUnit(w?.paddingTopPx);
  const r = clampWidgetPaddingUnit(w?.paddingRightPx);
  const b = clampWidgetPaddingUnit(w?.paddingBottomPx);
  const l = clampWidgetPaddingUnit(w?.paddingLeftPx);
  if (!t && !r && !b && !l) return null;
  const toRem = (u) => `${u * 0.125}rem`;
  return {
    paddingTop: t ? toRem(t) : undefined,
    paddingRight: r ? toRem(r) : undefined,
    paddingBottom: b ? toRem(b) : undefined,
    paddingLeft: l ? toRem(l) : undefined,
  };
}

/** Margen alrededor del marco del slot (unidades ±24 → ±3 rem). */
export function widgetOuterMarginStyle(w) {
  const t = clampWidgetMarginUnit(w?.marginTopPx);
  const r = clampWidgetMarginUnit(w?.marginRightPx);
  const b = clampWidgetMarginUnit(w?.marginBottomPx);
  const l = clampWidgetMarginUnit(w?.marginLeftPx);
  if (!t && !r && !b && !l) return null;
  const toRem = (u) => `${u * 0.125}rem`;
  return {
    marginTop: t ? toRem(t) : undefined,
    marginRight: r ? toRem(r) : undefined,
    marginBottom: b ? toRem(b) : undefined,
    marginLeft: l ? toRem(l) : undefined,
  };
}

export function clampImageBorderWidthPx(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(16, n));
}

export function clampImageShadowOffsetPx(v) {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(24, n));
}

function _hex6Or(c, fallback) {
  const s = String(c || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(s) ? s : fallback;
}

/** Borde + sombra paralela (varias capas en diagonal) para el marco de la imagen. */
export function imageWidgetFrameStyle(w) {
  if (w?.type !== "image") return {};
  const bw = clampImageBorderWidthPx(w.imageBorderWidthPx);
  const bc = _hex6Or(w.imageBorderColor, "#d4d4d4");
  const so = clampImageShadowOffsetPx(w.imageShadowOffsetPx);
  const scRaw = String(w.imageShadowColor || "").trim();
  const sc = scRaw || "rgba(15, 23, 42, 0.22)";
  const border = bw > 0 ? `${bw}px solid ${bc}` : undefined;
  let boxShadow;
  if (so > 0) {
    boxShadow = [1, 2, 3]
      .map((i) => {
        const d = Math.max(1, Math.round((so * i) / 3));
        return `${d}px ${d}px 0 0 ${sc}`;
      })
      .join(", ");
  }
  return {
    boxSizing: "border-box",
    ...(border ? { border } : {}),
    ...(boxShadow ? { boxShadow } : {}),
  };
}

/** Normaliza padding, margen y decoración de imagen en el árbol de widgets (incl. columnas). */
export function normalizeWidgetLayoutDeep(w) {
  if (!w || typeof w !== "object") return w;
  const baseW = _clampNum(w.w, 0.04, 1, 0.4);
  const baseH = _clampNum(w.h, 0.03, 1, 0.12);
  const baseFsl = normalizedFontSizeLevel(w.fontSizeLevel);
  const baseTa = ["left", "center", "right", "justify"].includes(w.textAlign) ? w.textAlign : "left";
  const baseVa = ["start", "center", "end"].includes(w.verticalAlign) ? w.verticalAlign : "start";
  const _rFsl = Number(w.fontSizeLevelResponsive);
  const _rTa = w.textAlignResponsive;
  const _rVa = w.verticalAlignResponsive;
  let out = {
    ...w,
    w: baseW,
    h: baseH,
    wResponsive: _clampNum(w.wResponsive, 0.04, 1, baseW),
    hResponsive: _clampNum(w.hResponsive, 0.03, 1, baseH),
    fontSizeLevelResponsive: Number.isFinite(_rFsl) ? normalizedFontSizeLevel(_rFsl) : baseFsl,
    textAlignResponsive: (_rTa && ["left", "center", "right", "justify"].includes(_rTa)) ? _rTa : baseTa,
    verticalAlignResponsive: (_rVa && ["start", "center", "end"].includes(_rVa)) ? _rVa : baseVa,
    paddingTopPx: clampWidgetPaddingUnit(w.paddingTopPx),
    paddingRightPx: clampWidgetPaddingUnit(w.paddingRightPx),
    paddingBottomPx: clampWidgetPaddingUnit(w.paddingBottomPx),
    paddingLeftPx: clampWidgetPaddingUnit(w.paddingLeftPx),
    marginTopPx: clampWidgetMarginUnit(w.marginTopPx),
    marginRightPx: clampWidgetMarginUnit(w.marginRightPx),
    marginBottomPx: clampWidgetMarginUnit(w.marginBottomPx),
    marginLeftPx: clampWidgetMarginUnit(w.marginLeftPx),
  };
  if (out.type === "image") {
    const { objectFit: _dropFit, frameShape: _dropFs, ...imgBase } = out;
    out = {
      ...imgBase,
      imageBoxShape: normalizedImageBoxShape(out.imageBoxShape),
      lockAspectRatio: out.lockAspectRatio !== false,
      imageBorderWidthPx: clampImageBorderWidthPx(out.imageBorderWidthPx),
      imageShadowOffsetPx: clampImageShadowOffsetPx(out.imageShadowOffsetPx),
      imageBorderColor: _hex6Or(out.imageBorderColor, "#d4d4d4"),
      imageShadowColor: String(out.imageShadowColor || "").trim() || "rgba(15, 23, 42, 0.22)",
    };
  }
  if (out.type === "divider") {
    const ls = String(out.lineStyle || "").trim().toLowerCase();
    out = { ...out, lineStyle: ["solid", "dashed", "dotted"].includes(ls) ? ls : "solid" };
  }
  if (out.type === "columns_row") {
    const d = parseColumnsRow(out.text);
    const cells = d.cells.map((col) => col.map((c) => normalizeWidgetLayoutDeep(c)));
    out = { ...out, text: stringifyColumnsRow({ columnCount: d.columnCount, cells }) };
  }
  return out;
}

export function newId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `ob-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Efecto al mostrar la diapositiva en la vista pública compartida. */
export const SLIDE_ENTER_EFFECTS = ["none", "confetti", "fireworks", "fireworks_ascii"];

/** Normaliza `slideEnterEffect` y migra el antiguo booleano `confettiOnEnter`. */
export function normalizedSlideEnterEffect(slide) {
  const raw = String(slide?.slideEnterEffect || "").trim().toLowerCase();
  if (raw === "confetti" || raw === "fireworks" || raw === "fireworks_ascii") return raw;
  if (slide?.confettiOnEnter === true) return "confetti";
  return "none";
}

/** Color del texto ASCII para `slideEnterEffect === "fireworks_ascii"` (hex #RRGGBB). */
export function normalizedFireworksAsciiColor(slide) {
  const c = String(slide?.fireworksAsciiColor || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(c)) return c;
  return "#facc15";
}

export function newSlide() {
  return {
    id: newId(),
    titulo: "",
    contenido: "",
    background: "#ffffff",
    backgroundImageSrc: "",
    backgroundImageFit: "cover",
    widgets: [],
    slideEnterEffect: "none",
    fireworksAsciiColor: "#facc15",
    ...getSlideLayout({}),
  };
}

/** URL o data:image seguros para fondo del lienzo. */
export function isSafeSlideBackgroundImageSrc(src) {
  const s = String(src || "").trim();
  if (!s) return false;
  return /^https?:\/\//i.test(s) || /^data:image\//i.test(s);
}

/**
 * Estilo CSS del fondo del lienzo (color sólido y/o imagen).
 * @param {object|null} slide
 * @param {string} [fallbackColor] si no hay slide o color inválido
 */
export function slideCanvasBackgroundStyle(slide, fallbackColor = "#ffffff") {
  const rawColor = slide && typeof slide.background === "string" ? slide.background : "";
  const color =
    rawColor.startsWith("#") && rawColor.length >= 4 ? rawColor : typeof fallbackColor === "string" && fallbackColor.startsWith("#") ? fallbackColor : "#ffffff";
  const img = slide && String(slide.backgroundImageSrc || "").trim();
  const fit = slide?.backgroundImageFit === "contain" ? "contain" : "cover";
  if (!isSafeSlideBackgroundImageSrc(img)) {
    return { background: color };
  }
  return {
    backgroundColor: color,
    backgroundImage: `url(${JSON.stringify(img)})`,
    backgroundSize: fit,
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  };
}

/** Tamaños iniciales compactos (w/h como fracción del bloque central). */
const WIDGET_DEFAULTS = {
  heading: {
    x: 0,
    y: 0,
    w: 0.52,
    h: 0.065,
    text: "Título",
    textAlign: "left",
    verticalAlign: "start",
    fontSizeLevel: 3,
    textColor: "#171717",
  },
  text: {
    x: 0,
    y: 0,
    w: 0.62,
    h: 0.09,
    text: "Párrafo",
    textAlign: "left",
    verticalAlign: "start",
    fontSizeLevel: 3,
    textColor: "#404040",
  },
  bullets: {
    x: 0,
    y: 0,
    w: 0.5,
    h: 0.11,
    text: "Primer punto\nSegundo punto",
    textAlign: "left",
    verticalAlign: "start",
    fontSizeLevel: 3,
    textColor: "#404040",
  },
  divider: { x: 0, y: 0, w: 0.55, h: 0.022, text: "", textAlign: "left", verticalAlign: "center", lineStyle: "solid" },
  shape: { x: 0, y: 0, w: 0.1, h: 0.12, text: "", accent: "#e0e7ff", textAlign: "left", verticalAlign: "start" },
  sticky: {
    x: 0,
    y: 0,
    w: 0.28,
    h: 0.09,
    text: "Nota",
    textAlign: "left",
    verticalAlign: "start",
    fontSizeLevel: 3,
    textColor: "#422006",
  },
  columns_row: {
    x: 0,
    y: 0,
    w: 0.94,
    h: 0.2,
    text: emptyColumnsRow(2),
    textAlign: "left",
    verticalAlign: "start",
  },
  embed_tiktok: {
    x: 0,
    y: 0,
    w: 0.42,
    h: 0.34,
    text: "https://www.tiktok.com/@usuario/video/0000000000000000000",
    textAlign: "left",
    verticalAlign: "start",
  },
  embed_instagram: {
    x: 0,
    y: 0,
    w: 0.42,
    h: 0.34,
    text: "https://www.instagram.com/p/CODE/",
    textAlign: "left",
    verticalAlign: "start",
  },
  emoji: {
    x: 0,
    y: 0,
    w: 0.18,
    h: 0.22,
    text: "🎉",
    textAlign: "center",
    verticalAlign: "center",
    fontSizeLevel: 3,
  },
  image: {
    x: 0,
    y: 0,
    w: 0.48,
    h: 0.13,
    text: "",
    imageSrc: "",
    imageBoxShape: "rect",
    borderRadius: 12,
    lockAspectRatio: true,
    imageBorderWidthPx: 0,
    imageBorderColor: "#d4d4d4",
    imageShadowOffsetPx: 0,
    imageShadowColor: "rgba(15, 23, 42, 0.22)",
    textAlign: "left",
    verticalAlign: "start",
    fontSizeLevel: 3,
    textColor: "#525252",
  },
  chips: {
    x: 0,
    y: 0,
    w: 0.72,
    h: 0.1,
    text: '{"items":[]}',
    textAlign: "left",
    verticalAlign: "start",
    fontSizeLevel: 3,
    textColor: "#5b21b6",
  },
};

export const WIDGET_PALETTE = [
  { type: "heading", label: "Título" },
  { type: "text", label: "Párrafo" },
  { type: "bullets", label: "Lista" },
  { type: "divider", label: "Línea" },
  { type: "shape", label: "Forma" },
  { type: "sticky", label: "Post-it" },
  { type: "columns_row", label: "Columnas" },
  { type: "image", label: "Imagen" },
  { type: "chips", label: "Enlaces" },
  { type: "embed_tiktok", label: "TikTok" },
  { type: "embed_instagram", label: "Instagram" },
  { type: "emoji", label: "Emoji" },
];

export function createWidget(type) {
  const d = WIDGET_DEFAULTS[type] || WIDGET_DEFAULTS.text;
  const z = Date.now() % 100000;
  const base = {
    id: newId(),
    type,
    x: d.x ?? 0,
    y: d.y ?? 0,
    w: d.w,
    h: d.h,
    wResponsive: d.w,
    hResponsive: d.h,
    fontSizeLevelResponsive: null,
    textAlignResponsive: null,
    verticalAlignResponsive: null,
    z,
    text: d.text ?? "",
    accent: d.accent ?? "",
    textAlign: d.textAlign || "left",
    verticalAlign: d.verticalAlign || "start",
    paddingTopPx: 0,
    paddingRightPx: 0,
    paddingBottomPx: 0,
    paddingLeftPx: 0,
    marginTopPx: 0,
    marginRightPx: 0,
    marginBottomPx: 0,
    marginLeftPx: 0,
  };
  const typoFromDefaults = (widgetType, defColor) => {
    const tc = String(d.textColor || "").trim();
    return {
      fontSizeLevel: normalizedFontSizeLevel(d.fontSizeLevel ?? 3),
      textColor: /^#[0-9a-fA-F]{6}$/.test(tc) ? tc : defColor,
    };
  };

  if (type === "image") {
    return {
      ...base,
      imageSrc: d.imageSrc ?? "",
      borderRadius: d.borderRadius ?? 12,
      imageBoxShape: normalizedImageBoxShape(d.imageBoxShape),
      lockAspectRatio: d.lockAspectRatio !== false,
      imageBorderWidthPx: clampImageBorderWidthPx(d.imageBorderWidthPx ?? 0),
      imageBorderColor: _hex6Or(d.imageBorderColor, "#d4d4d4"),
      imageShadowOffsetPx: clampImageShadowOffsetPx(d.imageShadowOffsetPx ?? 0),
      imageShadowColor: String(d.imageShadowColor || "").trim() || "rgba(15, 23, 42, 0.22)",
      ...typoFromDefaults("image", "#525252"),
    };
  }
  if (type === "chips") {
    return { ...base, text: d.text || '{"items":[]}', ...typoFromDefaults("chips", "#5b21b6") };
  }
  if (type === "heading" || type === "text") {
    const tc = String(d.textColor || "").trim();
    return {
      ...base,
      fontSizeLevel: normalizedFontSizeLevel(d.fontSizeLevel ?? 3),
      textColor: /^#[0-9a-fA-F]{6}$/.test(tc) ? tc : type === "heading" ? "#171717" : "#404040",
    };
  }
  if (type === "bullets" || type === "sticky") {
    return {
      ...base,
      ...typoFromDefaults(type, type === "sticky" ? "#422006" : "#404040"),
    };
  }
  if (type === "emoji") {
    return {
      ...base,
      text: d.text ?? "🎉",
      fontSizeLevel: normalizedFontSizeLevel(d.fontSizeLevel ?? 3),
    };
  }
  return base;
}

export function findWidgetById(list, id) {
  if (!id || !list?.length) return null;
  for (const w of list) {
    if (w.id === id) return w;
    if (w.type === "columns_row") {
      const d = parseColumnsRow(w.text);
      for (const col of d.cells) {
        const hit = findWidgetById(col, id);
        if (hit) return hit;
      }
    }
  }
  return null;
}

export function updateWidgetById(list, id, partial) {
  if (!list?.length) return list || [];
  return list.map((w) => updateWidgetNode(w, id, partial));
}

function updateWidgetNode(w, id, partial) {
  if (w.id === id) return { ...w, ...partial };
  if (w.type === "columns_row") {
    const d = parseColumnsRow(w.text);
    const cells = d.cells.map((col) => col.map((c) => updateWidgetNode(c, id, partial)));
    return { ...w, text: stringifyColumnsRow({ columnCount: d.columnCount, cells }) };
  }
  return w;
}

export function deleteWidgetFromTree(list, id) {
  if (!list?.length) return [];
  const out = [];
  for (const w of list) {
    if (w.id === id) continue;
    if (w.type === "columns_row") {
      const d = parseColumnsRow(w.text);
      const cells = d.cells.map((col) => deleteWidgetFromTree(col, id));
      out.push({ ...w, text: stringifyColumnsRow({ columnCount: d.columnCount, cells }) });
    } else {
      out.push(w);
    }
  }
  return out;
}

export function insertWidgetCloneAfter(list, id, clone) {
  const idx = list.findIndex((w) => w.id === id);
  if (idx >= 0) return [...list.slice(0, idx + 1), clone, ...list.slice(idx + 1)];
  return list.map((w) => {
    if (w.type !== "columns_row") return w;
    const d = parseColumnsRow(w.text);
    const cells = d.cells.map((col) => insertWidgetCloneAfter(col, id, clone));
    return { ...w, text: stringifyColumnsRow({ columnCount: d.columnCount, cells }) };
  });
}

export function duplicateWidgetInTree(list, id) {
  const w = findWidgetById(list, id);
  if (!w) return list;
  const clone = cloneWidgetTree(w);
  const nz = list.reduce((m, x) => Math.max(m, x.z || 0), 0) + 1;
  clone.z = nz;
  return insertWidgetCloneAfter(list, id, clone);
}

export function replaceColumnInColumnsRow(rootList, rowId, colIndex, columnWidgets) {
  const row = findWidgetById(rootList, rowId);
  if (!row || row.type !== "columns_row") return rootList;
  const d = parseColumnsRow(row.text);
  const cells = d.cells.map((c, i) => (i === colIndex ? columnWidgets : c));
  return updateWidgetById(rootList, rowId, { text: stringifyColumnsRow({ columnCount: d.columnCount, cells }) });
}

/** Raíz: sube z. Dentro de columnas: mueve al final del array (pinta encima). */
export function bringWidgetToFrontInTree(list, id, depth = 0) {
  if (!list?.length) return list || [];
  const idx = list.findIndex((w) => w.id === id);
  if (idx >= 0) {
    if (depth === 0) {
      const maxZ = list.reduce((m, w) => Math.max(m, w.z || 0), 0);
      return list.map((w) => (w.id === id ? { ...w, z: maxZ + 1 } : w));
    }
    const copy = [...list];
    const [node] = copy.splice(idx, 1);
    return [...copy, node];
  }
  return list.map((w) => {
    if (w.type !== "columns_row") return w;
    const d = parseColumnsRow(w.text);
    let touched = false;
    const cells = d.cells.map((col) => {
      const rec = bringWidgetToFrontInTree(col, id, depth + 1);
      if (rec !== col) touched = true;
      return rec;
    });
    if (!touched) return w;
    return { ...w, text: stringifyColumnsRow({ columnCount: d.columnCount, cells }) };
  });
}

/** Preset de color por tipo de widget (migración / defaults). */
export function typoPresetForWidgetType(type) {
  switch (type) {
    case "heading":
      return "heading";
    case "sticky":
      return "sticky";
    case "image":
      return "caption";
    case "chips":
      return "chip";
    default:
      return "body";
  }
}

function _swapZWidgets(a, b) {
  const az = a.z ?? 0;
  const bz = b.z ?? 0;
  return { a: { ...a, z: bz }, b: { ...b, z: az } };
}

/** Sube el widget una posición en el orden de la diapositiva (raíz por `z`; dentro de columna por orden en array). */
export function moveWidgetEarlierInTree(list, id) {
  if (!list?.length) return list || [];
  const rootIdx = list.findIndex((w) => w.id === id);
  if (rootIdx >= 0) {
    const sorted = sortWidgetsByZ(list);
    const si = sorted.findIndex((w) => w.id === id);
    if (si <= 0) return list;
    const a = sorted[si - 1];
    const b = sorted[si];
    const { a: aa, b: bb } = _swapZWidgets(a, b);
    return list.map((w) => {
      if (w.id === aa.id) return aa;
      if (w.id === bb.id) return bb;
      return w;
    });
  }
  return list.map((w) => {
    if (w.type !== "columns_row") return w;
    const d = parseColumnsRow(w.text);
    let touched = false;
    const cells = d.cells.map((col) => {
      const i = col.findIndex((x) => x.id === id);
      if (i <= 0) return col;
      touched = true;
      const next = [...col];
      [next[i - 1], next[i]] = [next[i], next[i - 1]];
      return next;
    });
    if (!touched) return w;
    return { ...w, text: stringifyColumnsRow({ columnCount: d.columnCount, cells }) };
  });
}

/** Baja el widget una posición (simétrico a `moveWidgetEarlierInTree`). */
export function moveWidgetLaterInTree(list, id) {
  if (!list?.length) return list || [];
  const rootIdx = list.findIndex((w) => w.id === id);
  if (rootIdx >= 0) {
    const sorted = sortWidgetsByZ(list);
    const si = sorted.findIndex((w) => w.id === id);
    if (si < 0 || si >= sorted.length - 1) return list;
    const a = sorted[si];
    const b = sorted[si + 1];
    const { a: aa, b: bb } = _swapZWidgets(a, b);
    return list.map((w) => {
      if (w.id === aa.id) return aa;
      if (w.id === bb.id) return bb;
      return w;
    });
  }
  return list.map((w) => {
    if (w.type !== "columns_row") return w;
    const d = parseColumnsRow(w.text);
    let touched = false;
    const cells = d.cells.map((col) => {
      const i = col.findIndex((x) => x.id === id);
      if (i < 0 || i >= col.length - 1) return col;
      touched = true;
      const next = [...col];
      [next[i], next[i + 1]] = [next[i + 1], next[i]];
      return next;
    });
    if (!touched) return w;
    return { ...w, text: stringifyColumnsRow({ columnCount: d.columnCount, cells }) };
  });
}

/** Reordena solo widgets raíz: `orderedIds` es el nuevo orden visual (índice → `z`). */
export function reorderRootWidgetsByOrderedIds(list, orderedIds) {
  if (!list?.length || !orderedIds?.length) return list || [];
  const pos = new Map(orderedIds.map((id, i) => [id, i]));
  return list.map((w) => {
    const p = pos.get(w.id);
    if (p === undefined) return w;
    return { ...w, z: p };
  });
}

/** Convierte `image_frame` antiguo en `image` con `imageBoxShape`. */
function migrateWidgetLegacyImage(w) {
  if (!w || typeof w !== "object" || w.type !== "image_frame") return w;
  const fs = w.frameShape;
  const imageBoxShape = fs === "circle" ? "circle" : fs === "square" ? "square" : "rect";
  const { frameShape: _fs, type: _t, objectFit: _of, ...rest } = w;
  return {
    ...rest,
    type: "image",
    imageBoxShape,
    lockAspectRatio: w.lockAspectRatio !== false,
  };
}

/** Convierte diapositivas antiguas (solo título + contenido) al modelo con widgets. */
export function migrateSlide(slide) {
  if (!slide || typeof slide !== "object") return newSlide();
  const id = slide.id || newId();
  const background = typeof slide.background === "string" && slide.background.startsWith("#") ? slide.background : "#ffffff";
  const backgroundImageSrc =
    typeof slide.backgroundImageSrc === "string" && isSafeSlideBackgroundImageSrc(slide.backgroundImageSrc)
      ? slide.backgroundImageSrc.trim()
      : "";
  const backgroundImageFit = slide.backgroundImageFit === "contain" ? "contain" : "cover";
  let widgets = Array.isArray(slide.widgets)
    ? slide.widgets.map((raw) => {
        const wasLegacyFrame = raw && raw.type === "image_frame";
        const w = migrateWidgetLegacyImage(raw);
        const isImage = w.type === "image";
        return {
          textAlign: "left",
          verticalAlign: "start",
          imageSrc: "",
          borderRadius: 0,
          ...w,
          textAlign: w.textAlign || "left",
          verticalAlign: w.verticalAlign || "start",
          imageSrc: w.imageSrc != null ? w.imageSrc : "",
          borderRadius: typeof w.borderRadius === "number" ? w.borderRadius : Number(w.borderRadius) || 0,
          ...(isImage
            ? {
                imageBoxShape: normalizedImageBoxShape(w.imageBoxShape),
                lockAspectRatio: w.lockAspectRatio !== false,
              }
            : {}),
          ...((isImage &&
            (Math.abs(Number(w.w) - 0.28) < 0.01 || (wasLegacyFrame && Math.abs(Number(w.w) - 0.26) < 0.01)))
            ? { w: 1.0 }
            : {}),
          ...(widgetSupportsTextTypography(w.type)
            ? {
                fontSizeLevel: normalizedFontSizeLevel(w.fontSizeLevel),
                textColor: onboardingTextColorFromWidget(w, typoPresetForWidgetType(w.type)),
              }
            : {}),
        };
      })
    : [];
  const titulo = typeof slide.titulo === "string" ? slide.titulo : "";
  const contenido = typeof slide.contenido === "string" ? slide.contenido : "";

  if (widgets.length === 0 && (titulo.trim() || contenido.trim())) {
    const base = [];
    if (titulo.trim()) {
      base.push({
        id: newId(),
        type: "heading",
        x: 0,
        y: 0,
        w: 0.72,
        h: 0.1,
        z: 1,
        text: titulo,
        accent: "",
        textAlign: "left",
        verticalAlign: "start",
        fontSizeLevel: 3,
        textColor: "#171717",
      });
    }
    if (contenido.trim()) {
      base.push({
        id: newId(),
        type: "text",
        x: 0,
        y: 0,
        w: 0.78,
        h: 0.36,
        z: 2,
        text: contenido,
        accent: "",
        textAlign: "left",
        verticalAlign: "start",
        fontSizeLevel: 3,
        textColor: "#404040",
      });
    }
    widgets = base;
  }

  widgets = widgets.map((w) => normalizeWidgetLayoutDeep(w));

  const merged = { ...slide, id, background, backgroundImageSrc, backgroundImageFit, widgets, titulo, contenido };
  const enterEffect = normalizedSlideEnterEffect(merged);
  const { confettiOnEnter: _legacyConfetti, ...restMerged } = merged;
  return {
    ...restMerged,
    ...getSlideLayout(merged),
    slideEnterEffect: enterEffect,
    fireworksAsciiColor: normalizedFireworksAsciiColor(merged),
    backgroundImageSrc,
    backgroundImageFit,
  };
}

export function sortWidgetsByZ(widgets) {
  return [...(widgets || [])].sort((a, b) => (a.z || 0) - (b.z || 0));
}

export function cloneSlideDeep(slide) {
  const s = JSON.parse(JSON.stringify(slide));
  s.id = newId();
  s.widgets = (s.widgets || []).map((w) => cloneWidgetTree(w));
  return s;
}
